import { useQuery } from '@tanstack/react-query';
import { notificationsApi } from '@/lib/api/notifications.api';
import { queryKeys } from '../query-keys';
import { useOrganizationStore } from '@/store/organization.store';
import type {
  Notification,
  NotificationQueryDto,
  NotificationPreference,
  NotificationSummary,
  PaginationParams,
} from '@scrambl/shared';

interface NotificationListResponse {
  data: Notification[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Hook to fetch notifications with filters
 * Note: Real-time updates via WebSocket update the cache directly
 */
export function useNotificationsQuery(
  query?: NotificationQueryDto & PaginationParams,
  options?: { enabled?: boolean }
) {
  const { currentOrganization } = useOrganizationStore();

  return useQuery<NotificationListResponse>({
    queryKey: queryKeys.notifications.list(query),
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('Organization ID is required');
      }
      return notificationsApi.getNotifications(query);
    },
    enabled: (options?.enabled !== false && !!currentOrganization?.id) ?? false,
    placeholderData: (previousData) => previousData,
    staleTime: 10000, // Consider data fresh for 10 seconds
    gcTime: 60000, // Keep in cache for 1 minute
  });
}

/**
 * Hook to fetch a single notification by ID
 */
export function useNotificationQuery(
  notificationId: string | undefined,
  options?: { enabled?: boolean }
) {
  const { currentOrganization } = useOrganizationStore();

  return useQuery<Notification>({
    queryKey: queryKeys.notifications.detail(notificationId!),
    queryFn: async () => {
      if (!notificationId) {
        throw new Error('Notification ID is required');
      }
      return notificationsApi.getNotification(notificationId);
    },
    enabled: (options?.enabled !== false && !!notificationId && !!currentOrganization?.id) ?? false,
  });
}

/**
 * Hook to fetch unread notification count
 * Note: Real-time updates are handled via WebSocket UNREAD_COUNT_UPDATED events
 * No polling needed - WebSocket provides instant updates
 */
export function useUnreadNotificationCountQuery(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganizationStore();

  return useQuery<{ count: number }>({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('Organization ID is required');
      }
      return notificationsApi.getUnreadCount();
    },
    enabled: (options?.enabled !== false && !!currentOrganization?.id) ?? false,
    staleTime: 30000, // Consider data fresh for 30 seconds (prevents refetch on remount)
    gcTime: 60000, // Keep in cache for 1 minute
    // No refetchInterval - WebSocket handles real-time updates
  });
}

/**
 * Hook to fetch notification summary
 * Note: Real-time updates via WebSocket invalidate this query when needed
 */
export function useNotificationSummaryQuery(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganizationStore();

  return useQuery<NotificationSummary>({
    queryKey: queryKeys.notifications.summary(),
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('Organization ID is required');
      }
      return notificationsApi.getSummary();
    },
    enabled: (options?.enabled !== false && !!currentOrganization?.id) ?? false,
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 60000, // Keep in cache for 1 minute
    // No refetchInterval - WebSocket handles real-time updates
  });
}

/**
 * Hook to fetch notification preferences
 */
export function useNotificationPreferencesQuery(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganizationStore();

  return useQuery<NotificationPreference[]>({
    queryKey: queryKeys.notifications.preferences(),
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('Organization ID is required');
      }
      return notificationsApi.getPreferences();
    },
    enabled: (options?.enabled !== false && !!currentOrganization?.id) ?? false,
  });
}
